import { PayloadAction } from "@reduxjs/toolkit";
import { logger } from "@tecma/logs-fe";
import { DateTime } from "luxon";
import { getCorrectDatesForConstraints } from "@tecma/date-picker-utils";
import { all, delay, put, select, takeLatest } from "redux-saga/effects";

import { dateSelectorActions } from "store/sagas/dates/dates.actions";
import { applicationStateSelectors } from "store/slices/applicationState/selectors";
import { applicationStateActions } from "store/slices/applicationState/slice";
import { dateSelectorStateActions } from "store/slices/dates/slice";
import { RentConstraints, StartEndDates } from "types/dates";
import { filtersStateSelectors } from "store/slices/filters/selector";
import { ActiveFilters } from "types/filters";

import { roomsActions } from "../rooms/rooms.actions";

function* elaborateDates(dates: StartEndDates) {
  const { startDate, endDate } = dates;
  const parsedEndDate = endDate ?? DateTime.now().toISODate();

  const rentConstraints: RentConstraints | undefined = yield select(
    applicationStateSelectors.rentConstraints,
  );
  if (!rentConstraints) {
    return dates;
  }
  const [newStartDate, newEndDate] = getCorrectDatesForConstraints(
    [startDate, parsedEndDate ?? endDate],
    {
      checkInDays: rentConstraints.checkInDays,
      minMonthsToStay: rentConstraints.minMonthsToStay,
    },
    startDate,
  );
  return { startDate: newStartDate, endDate: newEndDate };
}

function* setDatesSaga(startDate: string, endDate: string) {
  yield all([
    put(dateSelectorStateActions.updateStartDate(startDate)),
    put(dateSelectorStateActions.updateEndDate(endDate)),
  ]);
}

function* datesInitSaga(action: PayloadAction<StartEndDates>) {
  const { startDate, endDate }: StartEndDates = yield elaborateDates(
    action.payload,
  );
  try {
    yield setDatesSaga(startDate, endDate);

    const filters: ActiveFilters = yield select(
      filtersStateSelectors.activeFilters,
    );

    yield put(
      roomsActions.getRoomsByProjectRequested({
        dates: {
          startDate,
          endDate,
        },
        filters,
      }),
    );

    yield put(dateSelectorActions.datesInitCompleted());
  } catch (err) {
    yield put(dateSelectorActions.datesError(err));
  }
}

function* datesErrorSaga(action: PayloadAction<unknown>) {
  const error: unknown = yield action.payload;
  logger.error(error);
  yield all([
    put(applicationStateActions.applicationInitDone()),
    put(applicationStateActions.loadingStopRequired()),
  ]);
}

function* datesUpdateSaga(action: PayloadAction<StartEndDates>) {
  const { startDate, endDate }: StartEndDates = yield elaborateDates(
    action.payload,
  );
  yield setDatesSaga(startDate, endDate);
  yield put(roomsActions.getRoomsByProjectInProgress());

  yield delay(1000);

  const filters: ActiveFilters = yield select(
    filtersStateSelectors.activeFilters,
  );
  yield put(
    roomsActions.getRoomsByProjectRequested({
      dates: {
        startDate,
        endDate,
      },
      filters,
    }),
  );
  yield put(dateSelectorActions.datesUpdateCompleted());
  yield put(roomsActions.getRoomsByProjectDone());
}

function* firstAvailabilityDatesUpdate(action: PayloadAction<StartEndDates>) {
  const { startDate, endDate } = yield elaborateDates(action.payload);
  yield setDatesSaga(startDate, endDate);
  yield put(dateSelectorActions.datesUpdateCompleted());
}

export default function* datesSaga() {
  yield takeLatest(dateSelectorActions.datesInitRequested, datesInitSaga);
  yield takeLatest(dateSelectorActions.datesUpdateRequested, datesUpdateSaga);
  yield takeLatest(dateSelectorActions.datesError, datesErrorSaga);
  yield takeLatest(
    dateSelectorActions.firstAvailabilityDatesUpdateRequested,
    firstAvailabilityDatesUpdate,
  );
}
